import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState, useAppThunkDispatch } from '../redux/store'
import { setSelectedProgram } from '../redux/common-data-slice'
import { setMultiProgramConfirm } from '../redux/multi-program-confirmation-slice'
import { toast } from 'react-toastify'
import Helper from './helper'
import { useUpdateDataAction } from './update-details-hook'
import { profiler } from './profiler'

export const useDispatchAPIs = (patientId: string) => {
  const dispatch = useAppThunkDispatch()
  const _multiProgramConfirm = useSelector(
    (state: RootState) => state.multiProgramConfirm
  )
  const patientDetails = useSelector(
    (state: RootState) => state.patientDetail.patientDetail
  )

  const [value] = useState(_multiProgramConfirm)
  const { updatePatientData } = useUpdateDataAction(patientDetails.id)

  const dispatchRequiredStates = (res: any, overrideSelectedProgram?: string[]) => {
    profiler.start('setSelectedProgram dispatch')
    const programsToSet = (overrideSelectedProgram && overrideSelectedProgram.length > 0)
      ? overrideSelectedProgram
      : res.payload.data?.patient?.program_enrollments
          ?.filter((f: { selected: boolean }) => f.selected === true)
          .map((e: { program: any }) => e.program)
    dispatch(setSelectedProgram(programsToSet))
    profiler.end('setSelectedProgram dispatch')

    const pCounts = res.payload.data.patient.pending_action_counts || {}
    const programEnrollments = res.payload.data?.patient?.program_enrollments || []

    // Use backend-provided pending_action_counts if available (faster)
    // Only fall back to expensive frontend calculation if backend doesn't provide counts
    if (Object.keys(pCounts).length > 0) {
      profiler.start('setMultiProgramConfirm (FAST PATH - backend counts)')
      console.log(`[PROF] [INFO] Using backend pending_action_counts: ${JSON.stringify(pCounts)}`)
      const currPendingList = programEnrollments.map((e: any) => ({
        program: e.program,
        enrollment_id: e.enrollment_id,
        pending: pCounts[e.program] || 0,
        overridenCount: 0, // Backend should provide this if needed
      }))
      dispatch(setMultiProgramConfirm(currPendingList))
      profiler.end('setMultiProgramConfirm (FAST PATH - backend counts)')
    } else {
      // Backend is expected to always provide pending_action_counts on /details.
      // If missing, surface loudly and fall back to zero counts rather than doing a slow client-side recompute.
      console.error('[PROF] Missing pending_action_counts from backend — defaulting to zero. Investigate the response.')
      const zeroCounts = programEnrollments.map((e: any) => ({
        program: e.program,
        enrollment_id: e.enrollment_id,
        pending: 0,
        overridenCount: 0,
      }))
      dispatch(setMultiProgramConfirm(zeroCounts))
    }
  }

  const checkForStageChange = (tempMultiProgramConfirm: any) => {
    return _multiProgramConfirm.filter(
      (f) =>
        f.pending > 0 &&
        tempMultiProgramConfirm.find(
          (m: { program: string }) => m.program === f.program
        ).pending <= 0
    )
  }

  const setProgramConfirmCount = (
    prevValue: boolean,
    currentValue: boolean,
    input: string[],
    forceStageUpdate: boolean = false,
    overrideData: any[] = []
  ) => {
    if (forceStageUpdate === true) {
      // dispachAPIs()
    } else {
      if (prevValue === true && currentValue === false) {
        const temp: any = _multiProgramConfirm.map((e) => {
          return {
            ...e,
            pending: e.pending - input.filter((f) => f === e.program).length,
          }
        })

        dispatch(setMultiProgramConfirm(temp))
        if (
          temp.some((s: { pending: number }) => s.pending <= 0) ||
          temp.some((s: { overridenCount: number }) => s.overridenCount > 0)
        ) {
          const checks = checkForStageChange(temp)

          if (checks.length > 0) {
            if (patientDetails.needs_update === false) {
              updatePatientData({
                isMeasurement: true,
                isTreatments: true,
                isConditions: true,
              })
            } else {
              toast.error(
                'Patient profile needs to update',
                Helper.bottom_center()
              )
            }
          }
        }
      } else if (prevValue === false && currentValue === true) {
        dispatch(
          setMultiProgramConfirm(
            _multiProgramConfirm.map((e) => {
              return {
                ...e,
                pending:
                  e.pending + input.filter((f) => f === e.program).length,
              }
            })
          )
        )
      }
    }
  }

  const multipleProgramCounts = (data: any) => {
    const newSetData = _multiProgramConfirm
      .map((e) => {
        return {
          program: e.program,
          enrollment_id: e.enrollment_id,
          pending:
            e.pending -
            data.filter(
              (f: {
                programs: string | string[]
                current: any
                previous: any
              }) => f.programs.includes(e.program) && !f.current && f.previous
            ).length,
        }
      })
      .map((e) => {
        return {
          program: e.program,
          enrollment_id: e.enrollment_id,
          pending:
            e.pending +
            data.filter(
              (f: {
                programs: string | string[]
                current: any
                previous: any
              }) => f.programs.includes(e.program) && f.current && !f.previous
            ).length,
        }
      })
    dispatch(setMultiProgramConfirm(newSetData))

    if (newSetData.some((s: { pending: number }) => s.pending <= 0)) {
      // two checks - pending === 0 or overidecount !== 0
      const checks = checkForStageChange(newSetData)
      if (checks.length > 0) {
        if (patientDetails.needs_update === false) {
          updatePatientData({
            isMeasurement: true,
            isTreatments: true,
            isConditions: true,
          })
        } else {
          toast.error('Patient profile needs to update', Helper.bottom_center())
        }
      }
    }
  }

  return {
    value,
    //  dispachUpdateStageAPIsByHook,
    // dispatchPatientInfoByHook,
    dispatchRequiredStates,
    setProgramConfirmCount,
    multipleProgramCounts,
  }
}
